import { fetchRatesForBedbankOffersById } from 'actions/BedbankOfferActions'
import { fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import { fetchAvailableRatesForOffer, fetchOfferById } from 'actions/OfferActions'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useEffect } from 'react'
import { getBundledFlightItems } from 'checkout/selectors/view/flights'
import { getItemOfferId, isBedbankItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { groupBy, nonNullable } from 'lib/array/arrayUtils'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { getUserCartAccommodationItems } from 'selectors/userCart'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { isLEHotel } from 'lib/offer/offerTypes'

function useFetchHotelItemData() {
  const dispatch = useAppDispatch()

  const {
    regionCode,
    currencyCode,
    existingOrder,
    accommodationItems,
    itemStates,
    bundledFlightItems,
    offers,
  } = useAppSelector((state) => {
    return {
      regionCode: state.checkout.cart.regionCode,
      currencyCode: state.checkout.cart.currencyCode,
      existingOrder: state.checkout.cart.existingOrder,
      accommodationItems: getUserCartAccommodationItems(state),
      itemStates: state.auth.userCart.itemStates,
      bundledFlightItems: getBundledFlightItems(state),
      offers: state.offer.offers,
    }
  }, shallowEqual)

  const flightOrigin = bundledFlightItems[0]?.originAirportCode

  useEffect(() => {
    const offerIds = nonNullable([
      ...accommodationItems.map(getItemOfferId),
      ...(existingOrder?.items ?? []).map(item => item.offerId),
    ])
    // Fetching one by one as currently sold out offers are not returned via the multi-fetch endpoint
    offerIds.forEach(offerId => {
      dispatch(fetchOfferById(offerId))
    })
  }, [dispatch, accommodationItems, existingOrder])

  // Get calendar data and rates
  useEffect(() => {
    const instantBookingHotelItems = accommodationItems.filter(isInstantBookingLEHotelItem)
    const leHotelItemsByBookingKey = groupBy(instantBookingHotelItems, item => `${item.offerId}-${item.checkIn}-${item.checkOut}`)
    const leHotelItemGroups = leHotelItemsByBookingKey.values().map(items => ({
      offerId: items[0].offerId,
      items,
    })).toArray()

    leHotelItemGroups.forEach((itemGroup) => {
      const offer = offers[itemGroup.offerId]
      if (isLEHotel(offer)) {
        const { checkIn, checkOut } = itemGroup.items[0]
        const occupancies = itemGroup.items.map((item) => item.occupancy)
        const uniqueKeys = new Set<string>(itemGroup.items.map((item) => getPackageUniqueKey(item.packageId, item.duration, item.roomRateId)))

        dispatch(fetchCalendarsByOccupancy(offer.id, {
          occupants: occupancies,
          packages: (offer as App.HotelOffer).packages.filter(pkg => uniqueKeys.has(pkg.uniqueKey)),
          regionCode,
          flightOrigin,
        }))

        if (offer.type === OFFER_TYPE_ALWAYS_ON && checkIn && checkOut) {
          occupancies.forEach(occupancy => {
            dispatch(fetchAvailableRatesForOffer(
              offer, {
                occupants: [occupancy],
                checkIn,
                checkOut,
                currencyCode,
                flightOrigin,
              },
            ))
          })
        }
      }
    })

    const bedbankItems = accommodationItems.filter(isBedbankItem).filter(item => !itemStates[item.itemId]?.deleted)
    const bedbankItemsByBookingKey = groupBy(bedbankItems, item => {
      if (itemStates[item.itemId]?.saveForLater) {
        // Items saved for later should have their rates fetched individually
        return item.itemId
      } else {
        return `${item.offerId}-${item.checkIn}-${item.checkOut}-${item.roomRateId}`
      }
    })
    const bedbankItemGroups = bedbankItemsByBookingKey.values().map(items => ({
      offerId: items[0].offerId,
      items,
    })).toArray()

    bedbankItemGroups.forEach((itemGroup) => {
      const { checkIn, checkOut } = itemGroup.items[0]
      dispatch(fetchRatesForBedbankOffersById([itemGroup.offerId], itemGroup.items.map((item) => item.occupancy), checkIn, checkOut))
    })
  }, [accommodationItems, currencyCode, dispatch, flightOrigin, offers, regionCode, itemStates])
}

export default useFetchHotelItemData
